export const TaskStatus = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  WAITING: 'waiting',
  CANCELED: 'canceled',
} as const;

export const TaskHorizon = {
  NOW: 'now',
  NEXT: 'next',
  SOON: 'soon',
  LATER: 'later',
  SOMEDAY: 'someday',
} as const;

export const RecurrenceInterval = {
  NEVER: 'never',
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly'
} as const;

export const TeamMemberRole = {
  OWNER: 'owner',
  ADMIN: 'admin',
  MEMBER: 'member'
} as const;

export const CommentType = {
  COMMENT: 'comment',
  STATUS_CHANGE: 'status_change',
  ASSIGNMENT: 'assignment',
  CREATION: 'creation',
  UPDATE: 'update'
} as const;

export type TaskStatus = typeof TaskStatus[keyof typeof TaskStatus];
export type TaskHorizon = typeof TaskHorizon[keyof typeof TaskHorizon];
export type RecurrenceInterval = typeof RecurrenceInterval[keyof typeof RecurrenceInterval];
export type TeamMemberRole = typeof TeamMemberRole[keyof typeof TeamMemberRole];
export type CommentType = typeof CommentType[keyof typeof CommentType];

// Review entry: a prompt paired with its response
export interface ReviewEntry {
  prompt: string;
  response: string;
}

export interface UserPreferences {
  theme?: 'light' | 'dark' | 'system';
  [key: string]: unknown; // Allow additional preferences
}

export interface CommentMetadata {
  previous_value?: string;
  new_value?: string;
  field_name?: string;
  source?: string;
  changes?: Array<{ field: string; old_value: string | null; new_value: string | null }>;
  [key: string]: unknown; // Allow additional metadata
}

export interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  google_id?: string;
  username?: string;
  auth_method?: 'google' | 'password' | 'both';
  created_time: string;
  last_login_time: string;
  is_active: boolean;
  preferences: UserPreferences;
  google_calendar_enabled?: boolean;
  google_calendar_last_sync?: string;
  telegram_chat_id?: string;
  telegram_linked_time?: string;
  timezone?: string | null;
  about_me?: string | null;
  agent_notes?: string | null;
  context_digest?: string | null;
  context_digest_updated_time?: string | null;
}

export interface Team {
  id: string;
  name: string;
  display_name?: string;
  description?: string;
  owner_id: string;
  created_time: string;
  is_active: boolean;
  allow_member_invites: boolean;
  require_approval: boolean;
  allow_personal_task_visibility: boolean;
  join_code?: string;
  join_code_expires_at?: string;
}

export interface TeamMember {
  id: string;
  team_id: string;
  user_id: string;
  role: TeamMemberRole;
  joined_time: string;
  is_active: boolean;
  show_personal_tasks: boolean;
  personal_tasks_visible_to_team: boolean;
  user: User;
}

export interface TeamWithMembers extends Team {
  members: TeamMember[];
  member_count: number;
}

export interface TeamCreate {
  name: string;
  display_name?: string;
  description?: string;
  allow_member_invites?: boolean;
  require_approval?: boolean;
  allow_personal_task_visibility?: boolean;
}

export interface TeamUpdate {
  name?: string;
  display_name?: string;
  description?: string;
  allow_member_invites?: boolean;
  require_approval?: boolean;
  allow_personal_task_visibility?: boolean;
}

export interface TeamMemberUpdate {
  role?: TeamMemberRole;
  show_personal_tasks?: boolean;
  personal_tasks_visible_to_team?: boolean;
}

export interface JoinTeamRequest {
  join_code: string;
}

export interface Todo {
  id: string;
  title: string;
  description?: string;
  user_id: string;
  team_id?: string;
  created_time: string;
  last_modified_time: string;
  last_notified_time?: string;
  deadline?: string | null;
  completed_date?: string | null;
  status: TaskStatus;
  horizon?: TaskHorizon | null;
  recurrence_interval: RecurrenceInterval;
  tags: string[];
  next_step?: string;
  parent_id?: string;
  goal_id?: string;
  notes?: string;
  source?: string;
  creator_id?: string;
  assigned_to_id?: string;
  estimated_time_minutes?: string;
}

export interface TodoCreate {
  title: string;
  description?: string;
  deadline?: string | null;
  status?: TaskStatus;
  horizon?: TaskHorizon | null;
  recurrence_interval?: RecurrenceInterval;
  tags: string[];
  next_step?: string;
  parent_id?: string | null;
  goal_id?: string;
  team_id?: string;
  notes?: string;
  source?: string;
  creator_id?: string;
  assigned_to_id?: string;
  estimated_time_minutes?: string;
}

export interface TodoUpdate {
  title?: string;
  description?: string;
  deadline?: string | null;
  completed_date?: string | null;
  status?: TaskStatus;
  horizon?: TaskHorizon | null;
  recurrence_interval?: RecurrenceInterval;
  tags?: string[];
  next_step?: string;
  parent_id?: string | null;
  goal_id?: string;
  team_id?: string;
  notes?: string;
  source?: string;
  creator_id?: string;
  assigned_to_id?: string;
  estimated_time_minutes?: string;
}

// Week types for weekly review functionality
export interface Week {
  id: string;
  user_id: string;
  team_id?: string;
  year: number;
  week_number: number;
  start_date: string;
  end_date: string;
  created_time: string;
  updated_time: string;
  is_review_completed: boolean;
  review_completed_time?: string;
  goals: string[];
  reflections: ReviewEntry[];
  top_priorities: string[];
  review_notes?: string;
}

export interface WeekCreate {
  year: number;
  week_number: number;
  start_date: string;
  end_date: string;
  goals?: string[];
  reflections?: ReviewEntry[];
  top_priorities?: string[];
  review_notes?: string;
  team_id?: string;
}

export interface WeekUpdate {
  goals?: string[];
  reflections?: ReviewEntry[];
  top_priorities?: string[];
  review_notes?: string;
  is_review_completed?: boolean;
}

export interface WeekTasks {
  week: Week;
  active_tasks: Todo[];
  today_tasks: Todo[];
}

// Month types for monthly review functionality
export interface Month {
  id: string;
  user_id: string;
  team_id?: string;
  year: number;
  month_number: number;
  start_date: string;
  end_date: string;
  created_time: string;
  updated_time: string;
  is_review_completed: boolean;
  review_completed_time?: string;
  goals: string[];
  reflections: ReviewEntry[];
  top_priorities: string[];
  projects: string[];
  review_notes?: string;
}

export interface MonthCreate {
  year: number;
  month_number: number;
  start_date: string;
  end_date: string;
  goals?: string[];
  reflections?: ReviewEntry[];
  top_priorities?: string[];
  projects?: string[];
  review_notes?: string;
  team_id?: string;
}

export interface MonthUpdate {
  goals?: string[];
  reflections?: ReviewEntry[];
  top_priorities?: string[];
  projects?: string[];
  review_notes?: string;
  is_review_completed?: boolean;
}

export interface MonthTasks {
  month: Month;
  active_tasks: Todo[];
}

// Year types for yearly review functionality
export interface Year {
  id: string;
  user_id: string;
  team_id?: string;
  year: number;
  start_date: string;
  end_date: string;
  created_time: string;
  updated_time: string;
  is_review_completed: boolean;
  review_completed_time?: string;
  goals: string[];
  reflections: ReviewEntry[];
  top_priorities: string[];
  projects: string[];
  values: string[];
  vision?: string;
  review_notes?: string;
}

export interface YearCreate {
  year: number;
  start_date: string;
  end_date: string;
  goals?: string[];
  reflections?: ReviewEntry[];
  top_priorities?: string[];
  projects?: string[];
  values?: string[];
  vision?: string;
  review_notes?: string;
  team_id?: string;
}

export interface YearUpdate {
  goals?: string[];
  reflections?: ReviewEntry[];
  top_priorities?: string[];
  projects?: string[];
  values?: string[];
  vision?: string;
  review_notes?: string;
  is_review_completed?: boolean;
}

// Comment interfaces
export interface TodoComment {
  id: string;
  todo_id: string;
  user_id: string;
  content: string;
  created_time: string;
  updated_time: string;
  is_edited: boolean;
  is_deleted: boolean;
  comment_type: CommentType;
  comment_metadata: CommentMetadata;
  user: User;
}

export interface TodoCommentCreate {
  content: string;
  comment_type?: CommentType;
  comment_metadata?: CommentMetadata;
}

export interface TodoCommentUpdate {
  content?: string;
  comment_metadata?: CommentMetadata;
}

// Event interfaces for day schedule
export interface Event {
  id: string;
  user_id: string;
  team_id?: string;
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  todo_id?: string;
  color: string;
  all_day: boolean;
  location?: string;
  created_time: string;
  updated_time: string;
  todo?: Todo;
  
  // Google Calendar sync fields
  google_event_id?: string;
  google_calendar_id?: string;
  google_visibility?: string;
  is_from_google?: boolean;
  google_html_link?: string;
  google_sync_status?: string;
  google_last_synced?: string;
}

export interface EventCreate {
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  color?: string;
  all_day?: boolean;
  location?: string;
  todo_id?: string;
  team_id?: string;
}

export interface EventUpdate {
  title?: string;
  description?: string;
  start_time?: string;
  end_time?: string;
  color?: string;
  all_day?: boolean;
  location?: string;
  todo_id?: string;
  team_id?: string;
}

// Goal Types
export const GoalStatus = {
  ACTIVE: 'active',
  ACHIEVED: 'achieved',
  PAUSED: 'paused',
  ABANDONED: 'abandoned',
} as const;

export type GoalStatus = typeof GoalStatus[keyof typeof GoalStatus];

export interface Goal {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  status: GoalStatus;
  target_date?: string | null;
  created_time: string;
  updated_time: string;
}

export interface GoalWithLinkedItems extends Goal {
  habits: Habit[];
  todos: Todo[];
}

export interface GoalCreate {
  name: string;
  description?: string;
  status?: GoalStatus;
  target_date?: string | null;
}

export interface GoalUpdate {
  name?: string;
  description?: string;
  status?: GoalStatus;
  target_date?: string | null;
}

// Habit Types
export type HabitFrequency = 'daily' | 'times_per_week' | 'weekly';

export interface Habit {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  frequency_type: HabitFrequency;
  times_per_week: number;
  target_duration_minutes?: number;
  goal_id?: string;
  is_active: boolean;
  sort_order: number;
  created_time: string;
  updated_time: string;
}

export interface HabitCompletion {
  id: string;
  habit_id: string;
  completed_date: string;
  duration_minutes?: number;
  notes?: string;
  created_time: string;
}

export interface HabitWithCompletions extends Habit {
  completions: HabitCompletion[];
  current_streak: number;
  week_completions: number;
}

export interface HabitCreate {
  name: string;
  description?: string;
  frequency_type: HabitFrequency;
  times_per_week?: number;
  target_duration_minutes?: number;
  goal_id?: string | null;
  is_active?: boolean;
  sort_order?: number;
}

export interface HabitUpdate {
  name?: string;
  description?: string;
  frequency_type?: HabitFrequency;
  times_per_week?: number;
  target_duration_minutes?: number;
  goal_id?: string | null;
  is_active?: boolean;
  sort_order?: number;
}

// Journal Entry Types
export const JournalEntrySource = {
  FREE: 'free',
  DAILY_START: 'daily_start',
  DAILY_END: 'daily_end',
  DAILY_GENERAL: 'daily_general',
  WEEKLY_REVIEW: 'weekly_review',
  MONTHLY_REVIEW: 'monthly_review',
  YEARLY_REVIEW: 'yearly_review',
} as const;

export type JournalEntrySource = typeof JournalEntrySource[keyof typeof JournalEntrySource];

export interface JournalEntry {
  id: string;
  user_id: string;
  content: string;
  prompt?: string | null;
  source: JournalEntrySource;
  period_type?: string | null;
  period_id?: string | null;
  period_label?: string | null;
  mood?: string | null;
  energy?: string | null;
  created_time: string;
  updated_time: string;
}

export interface JournalEntryCreate {
  content: string;
  prompt?: string;
  source?: JournalEntrySource;
  period_type?: string;
  period_id?: string;
  period_label?: string;
  mood?: string;
  energy?: string;
}

export interface JournalEntryUpdate {
  content?: string;
  prompt?: string;
  mood?: string;
  energy?: string;
}

// Recurring Actions
export interface RecurringAction {
  id: string;
  name: string;
  prompt: string;
  cron_expression: string;
  timezone: string | null;
  status: 'active' | 'paused';
  system_action: string | null;
  last_triggered_time: string | null;
  trigger_count: number;
  created_time: string | null;
}

export interface RecurringActionCreate {
  name: string;
  prompt: string;
  cron_expression: string;
  timezone?: string;
  system_action?: string;
}

export interface RecurringActionUpdate {
  name?: string;
  prompt?: string;
  cron_expression?: string;
  timezone?: string;
}

// AI Triage Suggestions
export interface TaskTriageSuggestion {
  task_id: string;
  suggested_next_step: string | null;
  suggested_estimated_time: string | null;
  confidence: number;
}

// Modal Stack Management
export interface ModalStackContext {
  currentModal: string | null;
  returnToModal?: string | null;
  returnToPhase?: string | null;
  returnToState?: Record<string, unknown>;
}

export interface ModalStackHandler {
  openTaskModal: (todo: Todo, returnContext?: ModalStackContext) => void;
  closeTaskModal: () => void;
  returnToPreviousModal: () => void;
}

// Documents
export interface Document {
  id: string;
  user_id: string;
  title: string;
  body: string;
  owner: 'agent' | 'user';
  task_id?: string | null;
  is_pinned: boolean;
  agent_edited?: boolean;
  folder?: string | null;
  summary?: string | null;
  created_time: string;
  updated_time: string;
}

export interface DocumentCreate {
  title: string;
  body: string;
  owner?: 'agent' | 'user';
  task_id?: string | null;
  is_pinned?: boolean;
  folder?: string | null;
  summary?: string | null;
}

export interface DocumentUpdate {
  title?: string;
  body?: string;
  is_pinned?: boolean;
  folder?: string | null;
  summary?: string | null;
}

// Focus Session types
export const FocusSessionStatus = {
  PLANNING: 'planning',
  ACTIVE: 'active',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  ABANDONED: 'abandoned',
} as const;

export type FocusSessionStatus = typeof FocusSessionStatus[keyof typeof FocusSessionStatus];

export interface FocusSessionItem {
  id: string;
  session_id: string;
  todo_id: string | null;
  title: string;
  sort_order: number;
  is_completed: boolean;
  is_skipped: boolean;
  completed_time: string | null;
  created_time: string;
}

export interface FocusSession {
  id: string;
  user_id: string;
  status: FocusSessionStatus;
  planned_duration_minutes: number | null;
  started_time: string | null;
  completed_time: string | null;
  pause_started_time: string | null;
  total_pause_seconds: number;
  max_pause_seconds: number;
  notepad: string;
  summary: string | null;
  created_time: string;
  updated_time: string;
  items: FocusSessionItem[];
}

export interface FocusSessionCreate {
  planned_duration_minutes?: number | null;
  max_pause_seconds?: number;
}

export interface FocusSessionUpdate {
  notepad?: string;
  planned_duration_minutes?: number | null;
  max_pause_seconds?: number;
}

export interface FocusSessionItemCreate {
  todo_id?: string | null;
  title: string;
}